import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Drawer,
  Form,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  message,
  Tag,
  Alert,
} from "antd";
import { Sliders, Calendar as CalIcon, CheckSquare, XCircle, Zap } from "react-feather";
import dayjs from "dayjs";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";

const { Title, Text } = Typography;

const DOW = [
  { key: 1, label: "Mon" },
  { key: 2, label: "Tue" },
  { key: 3, label: "Wed" },
  { key: 4, label: "Thu" },
  { key: 5, label: "Fri" },
  { key: 6, label: "Sat" },
  { key: 0, label: "Sun" },
];

const drawerStyles = {
  header: { padding: "16px 18px" },
  body: { padding: "14px 18px 18px" },
  footer: { padding: "12px 18px" },
};

const currencySymbol = (code) => {
  if (code === "INR") return "₹";
  if (code === "GBP") return "£";
  if (code === "USD") return "$";
  return `${code} `;
};

const initialValues = () => ({
  dateRange: [dayjs().startOf("day"), dayjs().startOf("day")],
  dow: DOW.map((d) => d.key),
  rooms: [],
  ratePlans: [],
  availabilityEnabled: false,
  availabilityValue: 0,
  rateEnabled: false,
  rateValue: 0,
  stopSellEnabled: false,
  stopSellValue: false,
  ctaEnabled: false,
  ctaValue: false,
  ctdEnabled: false,
  ctdValue: false,
  minStayArrivalEnabled: false,
  minStayArrivalValue: 1,
  minStayThroughEnabled: false,
  minStayThroughValue: 1,
  maxStayEnabled: false,
  maxStayValue: 0,
});

export default function BulkUpdateDrawer({
  open,
  onClose,
  propertyId,
  rooms = [],
  currency = "INR",
  onApplied,
  api,
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [datePickerKey, setDatePickerKey] = useState(0);

  const roomOptions = useMemo(
    () =>
      (rooms || []).map((r) => ({
        label: `${r.name} (Total ${r.total_rooms || 0})`,
        value: r.pms_room_type_id,
      })),
    [rooms]
  );

  const selectedRooms = Form.useWatch("rooms", form) || [];

  const ratePlanOptions = useMemo(() => {
    const out = [];
    const selectedSet = new Set(selectedRooms || []);

    for (const r of rooms || []) {
      if (selectedSet.size && !selectedSet.has(r.pms_room_type_id)) continue;
      for (const rp of r.ratePlans || []) {
        out.push({
          label: `${r.name} → ${rp.title}`,
          value: rp.id,
        });
      }
    }

    const seen = new Set();
    return out.filter((x) => (seen.has(x.value) ? false : (seen.add(x.value), true)));
  }, [rooms, selectedRooms]);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(initialValues());
    setSubmitting(false);
    setDatePickerKey((n) => n + 1);
  }, [open, form]);

  const dateRange = Form.useWatch("dateRange", form);
  const dow = Form.useWatch("dow", form) || [];
  const roomsValue = Form.useWatch("rooms", form) || [];
  const ratePlansValue = Form.useWatch("ratePlans", form) || [];

  const availabilityEnabled = Form.useWatch("availabilityEnabled", form);
  const availabilityValue = Form.useWatch("availabilityValue", form);
  const rateEnabled = Form.useWatch("rateEnabled", form);
  const rateValue = Form.useWatch("rateValue", form);
  const stopSellEnabled = Form.useWatch("stopSellEnabled", form);
  const stopSellValue = Form.useWatch("stopSellValue", form);
  const ctaEnabled = Form.useWatch("ctaEnabled", form);
  const ctaValue = Form.useWatch("ctaValue", form);
  const ctdEnabled = Form.useWatch("ctdEnabled", form);
  const ctdValue = Form.useWatch("ctdValue", form);
  const minStayArrivalEnabled = Form.useWatch("minStayArrivalEnabled", form);
  const minStayArrivalValue = Form.useWatch("minStayArrivalValue", form);
  const minStayThroughEnabled = Form.useWatch("minStayThroughEnabled", form);
  const minStayThroughValue = Form.useWatch("minStayThroughValue", form);
  const maxStayEnabled = Form.useWatch("maxStayEnabled", form);
  const maxStayValue = Form.useWatch("maxStayValue", form);

  const selectedRoomCount = roomsValue.length;
  const selectedRateCount = ratePlansValue.length;

  useEffect(() => {
    if (!open) return;
    if (!rateEnabled) form.setFieldsValue({ ratePlans: [] });
  }, [rateEnabled, open, form]);

  const selectAllRooms = () => form.setFieldsValue({ rooms: roomOptions.map((x) => x.value) });
  const clearRooms = () => form.setFieldsValue({ rooms: [] });
  const selectAllRates = () => form.setFieldsValue({ ratePlans: ratePlanOptions.map((x) => x.value) });
  const clearRates = () => form.setFieldsValue({ ratePlans: [] });

  const setDowPreset = (type) => {
    if (type === "all") form.setFieldsValue({ dow: DOW.map((d) => d.key) });
    if (type === "weekdays") form.setFieldsValue({ dow: [1, 2, 3, 4, 5] });
    if (type === "weekends") form.setFieldsValue({ dow: [6, 0] });
  };

  const setDatePreset = (type) => {
    const start = dayjs().startOf("day");
    let nextRange = [start, start];

    if (type === "next7") nextRange = [start, start.add(6, "day")];
    if (type === "next30") nextRange = [start, start.add(29, "day")];

    form.setFieldsValue({ dateRange: nextRange });
    setDatePickerKey((n) => n + 1);
  };

  const hasAnyRestrictionEnabled =
    !!availabilityEnabled ||
    !!rateEnabled ||
    !!stopSellEnabled ||
    !!ctaEnabled ||
    !!ctdEnabled ||
    !!minStayArrivalEnabled ||
    !!minStayThroughEnabled ||
    !!maxStayEnabled;

  const matchedDateCount = useMemo(() => countMatchingDates(dateRange, dow), [dateRange, dow]);

  const preview = useMemo(() => {
    const v = form.getFieldsValue(true);

    const dr =
      v.dateRange?.[0] && v.dateRange?.[1]
        ? `${dayjs(v.dateRange[0]).format("DD MMM YYYY")} → ${dayjs(v.dateRange[1]).format("DD MMM YYYY")}`
        : "—";

    const days =
      (v.dow || []).length
        ? (v.dow || []).map((key) => DOW.find((d) => d.key === key)?.label || key).join(", ")
        : "—";

    const items = [];

    if (v.availabilityEnabled) {
      items.push({ k: "Availability", v: v.availabilityValue });
    }

    if (v.rateEnabled) {
      items.push({
        k: "Rate",
        v: `${currencySymbol(currency)}${Number(v.rateValue || 0).toLocaleString()}`,
      });
    }

    if (v.stopSellEnabled) items.push({ k: "Stop Sell", v: v.stopSellValue ? "On" : "Off" });
    if (v.ctaEnabled) items.push({ k: "Closed To Arrival", v: v.ctaValue ? "On" : "Off" });
    if (v.ctdEnabled) items.push({ k: "Closed To Departure", v: v.ctdValue ? "On" : "Off" });
    if (v.minStayArrivalEnabled) items.push({ k: "Min Stay Arrival", v: v.minStayArrivalValue });
    if (v.minStayThroughEnabled) items.push({ k: "Min Stay Through", v: v.minStayThroughValue });
    if (v.maxStayEnabled) items.push({ k: "Max Stay", v: v.maxStayValue });

    return {
      dateRange: dr,
      days,
      rooms: selectedRoomCount,
      rates: selectedRateCount,
      items,
    };
  }, [form, currency, selectedRoomCount, selectedRateCount]);

  const warnings = useMemo(() => {
    const out = [];

    if (availabilityEnabled && Number(availabilityValue || 0) === 0) {
      out.push("Availability is set to 0. This can close inventory for the selected room types.");
    }

    if (stopSellEnabled && stopSellValue) {
      out.push("Stop Sell is ON. Matching dates will not be sold.");
    }

    if (ctaEnabled && ctaValue) {
      out.push("Closed To Arrival is ON. Guests cannot check in on matching dates.");
    }

    if (ctdEnabled && ctdValue) {
      out.push("Closed To Departure is ON. Guests cannot check out on matching dates.");
    }

    if (rateEnabled && !selectedRateCount) {
      out.push("Rate update is enabled but no rate plans are selected.");
    }

    if (maxStayEnabled && Number(maxStayValue || 0) > 0) {
      if (minStayArrivalEnabled && Number(maxStayValue || 0) < Number(minStayArrivalValue || 0)) {
        out.push("Max Stay is lower than Min Stay Arrival.");
      }
      if (minStayThroughEnabled && Number(maxStayValue || 0) < Number(minStayThroughValue || 0)) {
        out.push("Max Stay is lower than Min Stay Through.");
      }
    }

    return out;
  }, [
    availabilityEnabled,
    availabilityValue,
    stopSellEnabled,
    stopSellValue,
    ctaEnabled,
    ctaValue,
    ctdEnabled,
    ctdValue,
    rateEnabled,
    selectedRateCount,
    maxStayEnabled,
    maxStayValue,
    minStayArrivalEnabled,
    minStayArrivalValue,
    minStayThroughEnabled,
    minStayThroughValue,
  ]);

  const dateInputValue =
    dateRange?.[0] && dateRange?.[1]
      ? `${dayjs(dateRange[0]).format("DD MMM YYYY")} - ${dayjs(dateRange[1]).format("DD MMM YYYY")}`
      : "";

  async function submit() {
    const v = await form.validateFields();

    if (!propertyId) return message.error("Missing property_id.");
    if (!v.dateRange?.[0] || !v.dateRange?.[1]) return message.error("Please select a date range.");
    if (!v.dow?.length) return message.error("Please select at least one day.");
    if (!v.rooms?.length) return message.error("Please select at least one room type.");

    if (!hasAnyRestrictionEnabled) {
      return message.error("Please turn on at least one change before applying.");
    }

    if (v.rateEnabled && !v.ratePlans?.length) {
      return message.error("Select at least one rate plan for rate updates.");
    }

    if (v.rateEnabled && (v.rateValue === null || v.rateValue === undefined || Number(v.rateValue) < 0)) {
      return message.error("Please enter a valid rate value.");
    }

    if (v.maxStayEnabled && Number(v.maxStayValue || 0) > 0) {
      if (v.minStayArrivalEnabled && Number(v.maxStayValue || 0) < Number(v.minStayArrivalValue || 0)) {
        return message.error("Max Stay cannot be less than Min Stay Arrival.");
      }
      if (v.minStayThroughEnabled && Number(v.maxStayValue || 0) < Number(v.minStayThroughValue || 0)) {
        return message.error("Max Stay cannot be less than Min Stay Through.");
      }
    }

    const updates = {};

    if (v.availabilityEnabled) updates.availability = Number(v.availabilityValue);
    if (v.rateEnabled) updates.rate_value = Number(v.rateValue);
    if (v.stopSellEnabled) updates.stop_sell = Boolean(v.stopSellValue);
    if (v.ctaEnabled) updates.closed_to_arrival = Boolean(v.ctaValue);
    if (v.ctdEnabled) updates.closed_to_departure = Boolean(v.ctdValue);
    if (v.minStayArrivalEnabled) updates.min_stay_arrival = Number(v.minStayArrivalValue);
    if (v.minStayThroughEnabled) updates.min_stay_through = Number(v.minStayThroughValue);
    if (v.maxStayEnabled) updates.max_stay = Number(v.maxStayValue);

    const payload = {
      property_id: propertyId,
      start_date: v.dateRange[0].format("YYYY-MM-DD"),
      end_date: v.dateRange[1].format("YYYY-MM-DD"),
      days_of_week: v.dow,
      room_type_ids: v.rooms,
      rate_plan_ids: v.rateEnabled ? v.ratePlans : [],
      currency,
      updates,
      push_to_channex: true,
    };

    setSubmitting(true);
    try {
      if (!api?.ariBulkUpdate) {
        throw new Error("channelsApi.ariBulkUpdate not provided to BulkUpdateDrawer");
      }

      await api.ariBulkUpdate(payload);

      message.success(`Bulk update applied to Channex for ${matchedDateCount} date(s).`);
      onClose?.();
      onApplied?.();
    } catch (e) {
      console.error(e);
      message.error(e.response?.data?.message || e.message || "Bulk update failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="50vw"
      zIndex={11050}
      styles={drawerStyles}
      destroyOnClose={false}
      title={
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(157,240,61,0.18)",
              border: "1px solid rgba(15,23,42,0.08)",
            }}
          >
            <Sliders size={18} />
          </div>

          <div style={{ flex: 1 }}>
            <Title level={4} style={{ margin: 0 }}>
              Bulk Update
            </Title>
            <Text type="secondary">
              Select dates, choose rooms, turn on the changes you want, then apply to Channex.
            </Text>

            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Tag color="blue" style={{ marginInlineStart: 0 }}>
                {currency}
              </Tag>
              <Tag style={{ marginInlineStart: 0 }}>{selectedRoomCount} rooms</Tag>
              <Tag style={{ marginInlineStart: 0 }}>{selectedRateCount} rate plans</Tag>
              <Tag style={{ marginInlineStart: 0 }}>{matchedDateCount} dates</Tag>
              {submitting && <Tag color="gold">Applying…</Tag>}
            </div>
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <Text type="secondary" style={{ fontWeight: 600 }}>
            Changes will be pushed to Channex
          </Text>
          <Space>
            <Button onClick={onClose} icon={<XCircle size={16} />}>
              Cancel
            </Button>
            <Button type="primary" loading={submitting} onClick={submit} icon={<Zap size={16} />}>
              Apply to Channex
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="dateRange" hidden>
          <input type="hidden" />
        </Form.Item>

        <div
          style={{
            border: "1px solid rgba(15,23,42,0.08)",
            background: "rgba(15,23,42,0.015)",
            borderRadius: 16,
            padding: 14,
          }}
        >
          <FieldBlock
            title="1. Select dates"
            subtitle="Choose the date range and the days on which this update should apply."
          />

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <Button size="small" onClick={() => setDatePreset("today")}>Today</Button>
            <Button size="small" onClick={() => setDatePreset("next7")}>Next 7 Days</Button>
            <Button size="small" onClick={() => setDatePreset("next30")}>Next 30 Days</Button>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CalIcon size={16} /> Date Range
              </span>
            </label>

            <DateRangePicker
              key={datePickerKey}
              initialSettings={{
                startDate: dateRange?.[0] ? dayjs(dateRange[0]).toDate() : dayjs().startOf("day").toDate(),
                endDate: dateRange?.[1] ? dayjs(dateRange[1]).toDate() : dayjs().startOf("day").toDate(),
                minDate: dayjs().startOf("day").toDate(),
                autoApply: true,
                alwaysShowCalendars: true,
                opens: "left",
                drops: "down",
                showDropdowns: true,
                parentEl: ".bulk-update-date-picker-host",
                locale: { format: "DD MMM YYYY" },
                isInvalidDate: (date) =>
                  dayjs(date.toDate ? date.toDate() : date).startOf("day").isBefore(dayjs().startOf("day")),
              }}
              onApply={(event, picker) => {
                form.setFieldsValue({
                  dateRange: [dayjs(picker.startDate.toDate()), dayjs(picker.endDate.toDate())],
                });
                setDatePickerKey((n) => n + 1);
              }}
            >
              <div className="bulk-update-date-picker-host" style={{ position: "relative" }}>
                <input
                  type="text"
                  className="form-control"
                  readOnly
                  value={dateInputValue}
                  style={{
                    width: "100%",
                    height: 40,
                    borderRadius: 8,
                    border: "1px solid #d9d9d9",
                    padding: "8px 12px",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                />
              </div>
            </DateRangePicker>

            <div style={{ marginTop: 6 }}>
              <Text type="secondary">Example: choose 01 May to 15 May, then keep only weekdays if needed.</Text>
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
              <label style={{ fontWeight: 700 }}>Days of week</label>
              <Space>
                <Button size="small" onClick={() => setDowPreset("all")}>All Days</Button>
                <Button size="small" onClick={() => setDowPreset("weekdays")}>Weekdays</Button>
                <Button size="small" onClick={() => setDowPreset("weekends")}>Weekends</Button>
              </Space>
            </div>

            <Form.Item name="dow" style={{ marginBottom: 0 }}>
              <Checkbox.Group style={{ width: "100%" }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {DOW.map((d) => (
                    <Checkbox key={d.key} value={d.key}>
                      {d.label}
                    </Checkbox>
                  ))}
                </div>
              </Checkbox.Group>
            </Form.Item>
          </div>
        </div>

        <Divider />

        <SectionHeader
          title="2. Select room types"
          subtitle={`${selectedRoomCount} selected`}
          right={
            <Space>
              <Button size="small" onClick={selectAllRooms} icon={<CheckSquare size={14} />}>
                Select All
              </Button>
              <Button size="small" onClick={clearRooms}>
                Clear
              </Button>
            </Space>
          }
        />

        <Form.Item
          name="rooms"
          rules={[{ required: true, message: "Select at least one room" }]}
          style={{ marginTop: 8 }}
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Search and select room types"
            options={roomOptions}
            optionFilterProp="label"
            style={{ width: "100%" }}
            getPopupContainer={(trigger) => trigger.parentElement}
          />
        </Form.Item>

        <Divider />

        <div>
          <FieldBlock
            title="3. Choose what to update"
            subtitle="Only the rows that are checked will be sent to Channex."
          />

          <div
            style={{
              marginTop: 10,
              border: "1px solid rgba(15,23,42,0.08)",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <RowToggle
              form={form}
              enabledName="availabilityEnabled"
              label="Availability"
              help="Set how many rooms are available to sell."
              right={
                <Form.Item name="availabilityValue" style={{ marginBottom: 0 }}>
                  <InputNumber min={0} disabled={!availabilityEnabled} style={{ width: 170 }} placeholder="Rooms" />
                </Form.Item>
              }
            />

            <Divider style={{ margin: 0 }} />

            <RowToggle
              form={form}
              enabledName="rateEnabled"
              label="Rate"
              help="Apply one rate value to the selected rate plans. Backend will map it to the correct occupancy for each plan."
              right={
                <Form.Item name="rateValue" style={{ marginBottom: 0 }}>
                  <InputNumber
                    min={0}
                    disabled={!rateEnabled}
                    style={{ width: 170 }}
                    placeholder={`${currencySymbol(currency)} amount`}
                  />
                </Form.Item>
              }
            />

            <Divider style={{ margin: 0 }} />

            <BooleanRule
              form={form}
              label="Stop Sell"
              help="Turn ON to stop selling the selected room types for matching dates."
              enabledName="stopSellEnabled"
              valueName="stopSellValue"
              danger={stopSellEnabled && stopSellValue}
            />
            <Divider style={{ margin: 0 }} />
            <BooleanRule
              form={form}
              label="Closed To Arrival"
              help="Turn ON to stop check-in on matching dates."
              enabledName="ctaEnabled"
              valueName="ctaValue"
              danger={ctaEnabled && ctaValue}
            />
            <Divider style={{ margin: 0 }} />
            <BooleanRule
              form={form}
              label="Closed To Departure"
              help="Turn ON to stop check-out on matching dates."
              enabledName="ctdEnabled"
              valueName="ctdValue"
              danger={ctdEnabled && ctdValue}
            />
            <Divider style={{ margin: 0 }} />

            <NumberRule
              form={form}
              label="Min Stay Arrival"
              help="Minimum nights when guest checks in on matching dates."
              enabledName="minStayArrivalEnabled"
              valueName="minStayArrivalValue"
              min={0}
              placeholder="Nights"
            />
            <Divider style={{ margin: 0 }} />
            <NumberRule
              form={form}
              label="Min Stay Through"
              help="Minimum nights when stay includes matching dates."
              enabledName="minStayThroughEnabled"
              valueName="minStayThroughValue"
              min={0}
              placeholder="Nights"
            />
            <Divider style={{ margin: 0 }} />
            <NumberRule
              form={form}
              label="Max Stay"
              help="Maximum number of nights allowed."
              enabledName="maxStayEnabled"
              valueName="maxStayValue"
              min={0}
              placeholder="Nights"
            />
          </div>
        </div>

        <Divider />

        <SectionHeader
          title="4. Select rate plans"
          subtitle={
            rateEnabled
              ? `${selectedRateCount} selected • backend will use each plan’s mapped occupancy rules`
              : `${selectedRateCount} selected`
          }
          right={
            <Space>
              <Button size="small" onClick={selectAllRates} disabled={!rateEnabled} icon={<CheckSquare size={14} />}>
                Select All
              </Button>
              <Button size="small" onClick={clearRates} disabled={!rateEnabled}>
                Clear
              </Button>
            </Space>
          }
        />

        <Form.Item name="ratePlans" style={{ marginTop: 8 }}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder={rateEnabled ? "Search rate plans" : "Enable Rate above to select rate plans"}
            options={ratePlanOptions}
            optionFilterProp="label"
            disabled={!rateEnabled}
            style={{ width: "100%" }}
            getPopupContainer={(trigger) => trigger.parentElement}
          />
        </Form.Item>

        <Divider />

        <div
          style={{
            borderRadius: 16,
            border: "1px solid rgba(15,23,42,0.08)",
            background: "rgba(15,23,42,0.015)",
            padding: 12,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 12 }}>
            <div style={{ fontWeight: 900 }}>Review before apply</div>
            <Text type="secondary" style={{ fontWeight: 700 }}>
              This is what will be sent to Channex
            </Text>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, fontSize: 13 }}>
            <Text type="secondary">Date range</Text>
            <Text style={{ fontWeight: 800 }}>{preview.dateRange}</Text>

            <Text type="secondary">Days</Text>
            <Text style={{ fontWeight: 800 }}>{preview.days}</Text>

            <Text type="secondary">Selected rooms</Text>
            <Text style={{ fontWeight: 800 }}>{preview.rooms}</Text>

            <Text type="secondary">Selected rate plans</Text>
            <Text style={{ fontWeight: 800 }}>{preview.rates}</Text>

            <Text type="secondary">Matched dates</Text>
            <Text style={{ fontWeight: 800 }}>{matchedDateCount}</Text>
          </div>

          <div style={{ marginTop: 10 }}>
            {(preview.items || []).length ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {preview.items.map((it) => (
                  <Tag key={it.k} style={{ marginInlineStart: 0, borderRadius: 999, padding: "4px 10px" }}>
                    <b>{it.k}:</b>&nbsp;{String(it.v)}
                  </Tag>
                ))}
              </div>
            ) : (
              <Text type="secondary">No changes selected yet.</Text>
            )}
          </div>
        </div>

        {!!warnings.length && (
          <div style={{ marginTop: 12 }}>
            <Alert
              type="warning"
              showIcon
              message="Please review these before applying"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              }
            />
          </div>
        )}

        {!hasAnyRestrictionEnabled && (
          <div style={{ marginTop: 10 }}>
            <Text type="danger" style={{ fontWeight: 700 }}>
              Turn on at least one change before applying.
            </Text>
          </div>
        )}
      </Form>
    </Drawer>
  );
}

function FieldBlock({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <Title level={5} style={{ margin: 0 }}>{title}</Title>
      <Text type="secondary" style={{ fontWeight: 600 }}>{subtitle}</Text>
    </div>
  );
}

function SectionHeader({ title, subtitle, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
      <div>
        <Title level={5} style={{ margin: 0 }}>{title}</Title>
        <Text type="secondary" style={{ fontWeight: 700 }}>{subtitle}</Text>
      </div>
      <div>{right}</div>
    </div>
  );
}

function RowToggle({ form, enabledName, label, help, right }) {
  const enabled = Form.useWatch(enabledName, form);

  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "12px 12px", alignItems: "center" }}>
      <div style={{ minWidth: 220 }}>
        <Form.Item name={enabledName} valuePropName="checked" style={{ marginBottom: 0 }}>
          <Checkbox style={{ fontWeight: 800 }}>{label}</Checkbox>
        </Form.Item>
        {help ? (
          <div style={{ marginTop: 4 }}>
            <Text type="secondary">{help}</Text>
          </div>
        ) : null}
      </div>
      <div style={{ opacity: enabled ? 1 : 0.55 }}>{right}</div>
    </div>
  );
}

function BooleanRule({ form, label, help, enabledName, valueName, danger = false }) {
  const enabled = Form.useWatch(enabledName, form);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 12px",
        background: danger ? "rgba(255,77,79,0.04)" : "transparent",
      }}
    >
      <div style={{ minWidth: 220 }}>
        <Form.Item name={enabledName} valuePropName="checked" style={{ marginBottom: 0 }}>
          <Checkbox style={{ fontWeight: 700 }}>{label}</Checkbox>
        </Form.Item>
        <div style={{ marginTop: 4 }}>
          <Text type="secondary">{help}</Text>
        </div>
      </div>
      <Form.Item name={valueName} valuePropName="checked" style={{ marginBottom: 0 }}>
        <Switch disabled={!enabled} checkedChildren="ON" unCheckedChildren="OFF" />
      </Form.Item>
    </div>
  );
}

function NumberRule({ form, label, help, enabledName, valueName, min = 0, placeholder = "Value" }) {
  const enabled = Form.useWatch(enabledName, form);

  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 12px", alignItems: "center" }}>
      <div style={{ minWidth: 220 }}>
        <Form.Item name={enabledName} valuePropName="checked" style={{ marginBottom: 0 }}>
          <Checkbox style={{ fontWeight: 700 }}>{label}</Checkbox>
        </Form.Item>
        <div style={{ marginTop: 4 }}>
          <Text type="secondary">{help}</Text>
        </div>
      </div>
      <Form.Item name={valueName} style={{ marginBottom: 0 }}>
        <InputNumber disabled={!enabled} min={min} style={{ width: 170 }} placeholder={placeholder} />
      </Form.Item>
    </div>
  );
}

function countMatchingDates(dateRange, selectedDow) {
  const start = dateRange?.[0];
  const end = dateRange?.[1];
  if (!start || !end || !selectedDow?.length) return 0;

  const allowedDays = new Set(selectedDow);
  let cursor = dayjs(start).startOf("day");
  const finish = dayjs(end).startOf("day");
  let total = 0;

  while (cursor.isBefore(finish) || cursor.isSame(finish, "day")) {
    if (allowedDays.has(cursor.day())) total += 1;
    cursor = cursor.add(1, "day");
  }

  return total;
}